type StreamOptions = { headers?: Record<string, string>; signal?: AbortSignal; scope?: string | null; transportEvents?: boolean };
type WorkerMessage = { id?: string; type: string; status?: number; streaming?: boolean; bytes?: Uint8Array };
type Pending = { receive: (message: WorkerMessage) => void; fail: (error: Error) => void };
let worker: SharedWorker | null = null;
let unavailable = false;
let serial = 0;
const requests = new Map<string, Pending>();
const privateScope = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function" ? crypto.randomUUID() : `tab-${Math.random()}`;

function detach() {
  if (requests.size || !worker) return;
  worker.port.postMessage({ type: "detach" });
  worker.port.close();
  worker = null;
}

function getWorker(): SharedWorker | null {
  if (unavailable || typeof SharedWorker === "undefined") return null;
  if (worker) return worker;
  try {
    const base = import.meta.env?.BASE_URL || "/";
    const url = new URL("mq-event-stream-worker.js", new URL(base, window.location.href));
    worker = new SharedWorker(url, { name: "mq-event-stream-v1", type: "classic" });
    const instance = worker;
    instance.port.onmessage = ({ data }: MessageEvent<WorkerMessage>) => {
      if (data.type === "ping") { instance.port.postMessage({ type: "pong" }); return; }
      if (data.id) requests.get(data.id)?.receive(data);
    };
    const fail = () => {
      if (worker !== instance) return;
      unavailable = true;
      for (const request of [...requests.values()]) request.fail(new Error("Shared event transport interrupted"));
      requests.clear(); instance.port.close();
      if (worker === instance) worker = null;
    };
    instance.onerror = fail;
    instance.port.onmessageerror = fail;
    instance.port.addEventListener("close", () => {
      if (worker !== instance) return;
      worker = null;
      for (const request of [...requests.values()]) request.fail(new Error("Shared event port closed"));
    });
    instance.port.start();
    return instance;
  } catch {
    unavailable = true;
    return null;
  }
}

/** Same response/body contract as fetch, with one bounded origin-wide connection owner. */
export async function openSharedEventStream(url: string, options: StreamOptions = {}): Promise<Response> {
  if (options.signal?.aborted) throw options.signal.reason;
  const absolute = new URL(url, window.location.origin);
  if (absolute.origin !== window.location.origin || !absolute.pathname.startsWith("/api/")) throw new Error("Event streams must use a same-origin API endpoint");
  const instance = getWorker();
  if (!instance) {
    // The server ends this lease even if browser timers stop in a frozen tab.
    return fetch(url, {
      headers: { ...options.headers, Accept: "text/event-stream", "X-Mqorva-Stream-Lease": "bounded" },
      signal: options.signal, credentials: "same-origin", cache: "no-store",
    });
  }
  const id = `${++serial}`;
  try {
    return await new Promise<Response>((resolve, reject) => {
      let opened = false;
      let controller: ReadableStreamDefaultController<Uint8Array> | null = null;
      const release = () => {
        if (!requests.delete(id)) return;
        clearTimeout(handshake);
        options.signal?.removeEventListener("abort", abort);
        instance.port.postMessage({ type: "unsubscribe", id });
        detach();
      };
      const abort = () => {
        const error = options.signal?.reason ?? new DOMException("Aborted", "AbortError");
        if (opened) controller?.error(error); else reject(error);
        release();
      };
      const handshake = window.setTimeout(() => {
        unavailable = true;
        if (opened) controller?.error(new Error("Event worker unavailable"));
        else reject(new Error("Event worker unavailable"));
        release();
      }, 3000);
      requests.set(id, {
        receive(message) {
          if (message.type === "accepted") { clearTimeout(handshake); return; }
          if (message.type === "response") {
            clearTimeout(handshake);
            if (!message.streaming) {
              if (opened) controller?.close();
              else resolve(new Response(null, { status: message.status ?? 503 }));
              release(); return;
            }
            const body = new ReadableStream<Uint8Array>({
              start(next) { controller = next; },
              cancel() { release(); },
            });
            opened = true;
            resolve(new Response(body, { status: message.status ?? 200, headers: { "Content-Type": "text/event-stream" } }));
          } else if (message.type === "chunk" && message.bytes) {
            controller?.enqueue(message.bytes);
            instance.port.postMessage({ type: "ack", id });
          } else if ((message.type === "reconnecting" || message.type === "reconnected") && opened && options.transportEvents) {
            controller?.enqueue(new TextEncoder().encode(`event: mq.transport.${message.type}\ndata: {}\n\n`));
          } else if (message.type === "end") {
            if (opened) controller?.close(); else reject(new Error("Event stream interrupted before response"));
            release();
          }
        },
        fail(error) {
          if (opened) controller?.error(error); else reject(error);
          release();
        },
      });
      options.signal?.addEventListener("abort", abort, { once: true });
      instance.port.postMessage({ type: "subscribe", id, url: absolute.href,
        headers: Array.from(new Headers(options.headers).entries()).sort(([left], [right]) => left.localeCompare(right)),
        scope: options.scope ?? (new Headers(options.headers).has("Authorization") ? "header-authenticated" : privateScope) });
    });
  } catch (error) {
    if (unavailable && !options.signal?.aborted) return openSharedEventStream(url, options);
    throw error;
  }
}

if (typeof window !== "undefined") window.addEventListener("pagehide", () => {
  for (const request of [...requests.values()]) request.fail(new Error("Event page suspended"));
});
